import {UseFormReturn} from "react-hook-form";

import {Button} from "@/components/ui/button";
import {Input} from "@/components/ui/input";
import {Label} from "@/components/ui/label";
import {Separator} from "@/components/ui/separator";

import {Eye, EyeOff, KeyRound, Lock, RefreshCw, ShieldCheck} from "lucide-react";

interface PasswordUpdateFormProps {
    form: UseFormReturn<{ oldPassword: string; newPassword: string; confirmPassword: string }>;
    isLoadingPassword: boolean;
    showPasswords: { old: boolean; new: boolean };
    togglePasswordVisibility: (field: "old" | "new") => void;
    handlePasswordUpdate: (data: {
        oldPassword?: string;
        newPassword: string;
        confirmPassword: string;
    }) => Promise<void>;
}

export default function PasswordUpdateForm({
                                               form,
                                               isLoadingPassword,
                                               showPasswords,
                                               togglePasswordVisibility,
                                               handlePasswordUpdate,
                                           }: PasswordUpdateFormProps) {
    const {
        register,
        handleSubmit,
        formState: {errors, isDirty},
    } = form;

    const hasAnyError = Boolean(errors.oldPassword || errors.newPassword || errors.confirmPassword);

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
                <div className="space-y-1">
                    <div className="text-sm font-semibold">Смена пароля</div>
                    <div className="text-xs text-muted-foreground">
                        Используй минимум 6 символов. После смены пароль станет активным сразу
                    </div>
                </div>

                <div
                    className="inline-flex items-center gap-2 rounded-full border bg-muted/20 px-3 py-1 text-xs text-muted-foreground">
                    <ShieldCheck className="h-4 w-4"/>
                    Безопасность
                </div>
            </div>

            <Separator/>

            <form onSubmit={handleSubmit(handlePasswordUpdate)} className="space-y-4">
                {/* Old password */}
                <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Старый пароль</Label>

                    <div className="relative">
                        <KeyRound className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"/>
                        <Input
                            className={[
                                "pl-10 pr-10",
                                errors.oldPassword ? "border-destructive focus-visible:ring-destructive" : "",
                            ].join(" ")}
                            type={showPasswords.old ? "text" : "password"}
                            placeholder="Введи старый пароль"
                            {...register("oldPassword", {
                                required: "Введите старый пароль",
                                minLength: {value: 6, message: "Минимум 6 символов"},
                            })}
                        />

                        <button
                            type="button"
                            onClick={() => togglePasswordVisibility("old")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted/30"
                            aria-label={showPasswords.old ? "Скрыть пароль" : "Показать пароль"}
                        >
                            {showPasswords.old ? <EyeOff className="h-4 w-4"/> : <Eye className="h-4 w-4"/>}
                        </button>
                    </div>

                    {errors.oldPassword?.message ? (
                        <p className="text-xs text-destructive">{errors.oldPassword.message as string}</p>
                    ) : null}
                </div>

                {/* New password */}
                <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Новый пароль</Label>

                    <div className="relative">
                        <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"/>
                        <Input
                            className={[
                                "pl-10 pr-10",
                                errors.newPassword ? "border-destructive focus-visible:ring-destructive" : "",
                            ].join(" ")}
                            type={showPasswords.new ? "text" : "password"}
                            placeholder="Придумай новый пароль"
                            {...register("newPassword", {
                                required: "Введите новый пароль",
                                minLength: {value: 6, message: "Минимум 6 символов"},
                            })}
                        />

                        <button
                            type="button"
                            onClick={() => togglePasswordVisibility("new")}
                            className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted/30"
                            aria-label={showPasswords.new ? "Скрыть пароль" : "Показать пароль"}
                        >
                            {showPasswords.new ? <EyeOff className="h-4 w-4"/> : <Eye className="h-4 w-4"/>}
                        </button>
                    </div>

                    {errors.newPassword?.message ? (
                        <p className="text-xs text-destructive">{errors.newPassword.message as string}</p>
                    ) : (
                        <p className="text-xs text-muted-foreground">Минимум 6 символов</p>
                    )}
                </div>

                {/* Confirm password */}
                <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Подтверждение</Label>

                    <div className="relative">
                        <Lock className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"/>
                        <Input
                            className={[
                                "pl-10",
                                errors.confirmPassword ? "border-destructive focus-visible:ring-destructive" : "",
                            ].join(" ")}
                            type={showPasswords.new ? "text" : "password"}
                            placeholder="Повтори новый пароль"
                            {...register("confirmPassword", {
                                required: "Подтвердите новый пароль",
                                minLength: {value: 6, message: "Минимум 6 символов"},
                            })}
                        />
                    </div>

                    {errors.confirmPassword?.message ? (
                        <p className="text-xs text-destructive">{errors.confirmPassword.message as string}</p>
                    ) : null}
                </div>

                {/* Footer */}
                <div className="pt-1 flex flex-col gap-2 sm:flex-row sm:justify-end">
                    <Button
                        type="submit"
                        disabled={!isDirty || isLoadingPassword || hasAnyError}
                        className="sm:min-w-[220px]"
                    >
                        {isLoadingPassword ? (
                            <>
                                <RefreshCw className="mr-2 h-4 w-4 animate-spin"/>
                                Обновляем
                            </>
                        ) : (
                            "Обновить пароль"
                        )}
                    </Button>
                </div>
            </form>
        </div>
    );
}
